import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Calculator, 
  Home, 
  Check,
  ChevronRight,
  ArrowLeft
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const propertyTypes = [
  { id: "studio", label: "Studio", icon: "🏠" },
  { id: "1bed", label: "1 Bedroom", icon: "🏠" },
  { id: "2bed", label: "2 Bedrooms", icon: "🏡" },
  { id: "3bed", label: "3 Bedrooms", icon: "🏡" },
  { id: "4bed", label: "4 Bedrooms", icon: "🏘️" },
  { id: "5plus", label: "5+ Bedrooms", icon: "🏰" }
];

const furnishedOptions = [
  { id: "unfurnished", label: "Unfurnished" },
  { id: "part-furnished", label: "Part Furnished" },
  { id: "furnished", label: "Fully Furnished" }
];

const serviceOptions = [
  { id: "inventory", label: "Inventory Report", description: "Full property documentation" },
  { id: "check-in", label: "Check-In", description: "Tenant move-in inspection" },
  { id: "check-out", label: "Check-Out", description: "End of tenancy inspection" },
  { id: "mid-term", label: "Mid-Term Inspection", description: "Periodic property check" },
  { id: "snagging", label: "Snagging Survey", description: "New build defect inspection" }
];

const QuoteCalculator = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    property_type: "",
    furnished: "",
    services: [],
    postcode: "",
    name: "",
    email: "",
    phone: ""
  });
  const [quote, setQuote] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const handlePropertySelect = (type) => {
    setFormData({ ...formData, property_type: type });
  };

  const handleFurnishedSelect = (option) => {
    setFormData({ ...formData, furnished: option });
  };

  const handleServiceToggle = (serviceId) => {
    const services = formData.services.includes(serviceId)
      ? formData.services.filter(s => s !== serviceId)
      : [...formData.services, serviceId];
    setFormData({ ...formData, services });
  };

  const calculateQuote = async () => {
    if (!formData.property_type || !formData.furnished || formData.services.length === 0) {
      toast.error("Please complete all selections");
      return;
    }

    setIsCalculating(true);
    try {
      const response = await axios.post(`${API}/quotes`, formData);
      setQuote(response.data);
      setStep(4);
    } catch (error) {
      toast.error("Failed to calculate quote");
    } finally {
      setIsCalculating(false);
    }
  };

  const handleBookNow = () => {
    // Pass quote data to booking page
    navigate("/book", { state: { quote, formData } });
  };

  const canProceed = () => {
    if (step === 1) return formData.property_type !== "";
    if (step === 2) return formData.furnished !== "";
    if (step === 3) return formData.services.length > 0;
    return true;
  };

  return (
    <div className="min-h-screen bg-black" data-testid="quote-calculator-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-neutral-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-20">
            <Link to="/" data-testid="logo">
              <img 
                src="https://customer-assets.emergentagent.com/job_property-hub-465/artifacts/aqf0sdon_AIP1%20No%20Background.png" 
                alt="AIP Logo" 
                className="h-14 w-auto"
              />
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-neutral-400 hover:text-white">
                <ArrowLeft className="mr-2" size={18} /> Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-32 pb-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-neutral-900 border border-neutral-800 mb-6">
              <Calculator className="text-[#B8960B]" size={28} />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Instant Quote Calculator</h1>
            <p className="text-neutral-400">Get an accurate price estimate in seconds</p>
          </motion.div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-12">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${
                  step >= s ? 'bg-[#B8960B] text-white' : 'bg-neutral-800 text-neutral-500'
                }`}>
                  {step > s ? <Check size={18} /> : s}
                </div>
                {s < 4 && (
                  <div className={`w-12 md:w-20 h-1 mx-2 transition-colors ${
                    step > s ? 'bg-[#B8960B]' : 'bg-neutral-800'
                  }`} />
                )}
              </div>
            ))}
          </div>

          {/* Step 1: Property Type */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-xl font-semibold mb-6 text-center">Select Property Type</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {propertyTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => handlePropertySelect(type.id)}
                    className={`p-6 rounded-xl border transition-all text-center ${
                      formData.property_type === type.id
                        ? 'border-[#B8960B] bg-[#B8960B]/10'
                        : 'border-neutral-800 bg-neutral-900/50 hover:border-neutral-700'
                    }`}
                    data-testid={`property-type-${type.id}`}
                  >
                    <span className="text-3xl mb-2 block">{type.icon}</span>
                    <span className="font-medium">{type.label}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Furnished Status */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-xl font-semibold mb-6 text-center">Is the property furnished?</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {furnishedOptions.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => handleFurnishedSelect(option.id)}
                    className={`p-6 rounded-xl border transition-all text-center ${
                      formData.furnished === option.id
                        ? 'border-[#B8960B] bg-[#B8960B]/10'
                        : 'border-neutral-800 bg-neutral-900/50 hover:border-neutral-700'
                    }`}
                    data-testid={`furnished-${option.id}`}
                  >
                    <Home className={`mx-auto mb-3 ${formData.furnished === option.id ? 'text-[#B8960B]' : 'text-neutral-500'}`} size={28} />
                    <span className="font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 3: Services */}
          {step === 3 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <h2 className="text-xl font-semibold mb-6 text-center">Select Services Required</h2>
              <div className="space-y-3">
                {serviceOptions.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => handleServiceToggle(service.id)}
                    className={`w-full p-5 rounded-xl border transition-all text-left flex items-center gap-4 ${
                      formData.services.includes(service.id)
                        ? 'border-[#B8960B] bg-[#B8960B]/10'
                        : 'border-neutral-800 bg-neutral-900/50 hover:border-neutral-700'
                    }`}
                    data-testid={`service-${service.id}`}
                  >
                    <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                      formData.services.includes(service.id)
                        ? 'border-[#B8960B] bg-[#B8960B]'
                        : 'border-neutral-600'
                    }`}>
                      {formData.services.includes(service.id) && <Check size={14} className="text-white" />}
                    </div>
                    <div>
                      <div className="font-medium">{service.label}</div>
                      <div className="text-sm text-neutral-500">{service.description}</div>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 4: Quote Result */}
          {step === 4 && quote && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl p-8 mb-8">
                <h2 className="text-xl font-semibold mb-2">Your Estimated Quote</h2>
                <div className="text-5xl font-bold text-[#B8960B] mb-6">
                  £{quote.total_price.toFixed(2)}
                </div>
                
                <div className="border-t border-neutral-800 pt-6 mt-6">
                  <h3 className="text-sm font-semibold text-neutral-400 mb-4 uppercase tracking-wider">Price Breakdown</h3>
                  <div className="space-y-3">
                    {Object.entries(quote.breakdown).map(([service, price]) => (
                      <div key={service} className="flex justify-between items-center">
                        <span className="text-neutral-400 capitalize">{service.replace('-', ' ')}</span>
                        <span className="font-medium">£{price.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={handleBookNow}
                  className="btn-primary rounded-full px-8 py-6 text-lg"
                  data-testid="book-now-btn"
                >
                  Book Now <ChevronRight className="ml-2" size={20} />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setStep(1);
                    setQuote(null);
                    setFormData({ ...formData, services: [] });
                  }}
                  className="btn-secondary rounded-full px-8 py-6 text-lg text-neutral-300"
                >
                  Start Over
                </Button>
              </div>
            </motion.div>
          )}

          {/* Navigation Buttons */}
          {step < 4 && (
            <div className="flex justify-between mt-10">
              <Button
                variant="outline"
                onClick={() => setStep(Math.max(1, step - 1))}
                disabled={step === 1}
                className="btn-secondary rounded-full px-6 py-5 text-neutral-300"
              >
                Back
              </Button>
              
              {step < 3 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={!canProceed()}
                  className="btn-primary rounded-full px-6 py-5"
                  data-testid="next-step-btn"
                >
                  Continue <ChevronRight className="ml-2" size={18} />
                </Button>
              ) : (
                <Button
                  onClick={calculateQuote}
                  disabled={!canProceed() || isCalculating}
                  className="btn-primary rounded-full px-6 py-5"
                  data-testid="calculate-quote-btn"
                >
                  {isCalculating ? "Calculating..." : "Get Quote"} 
                  <ChevronRight className="ml-2" size={18} />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteCalculator;
